import React, { useEffect, useState } from 'react';
import { Box, HStack, IconButton, Icon, Text } from '@chakra-ui/react';
import moment from 'moment';
import { ChevronLeft, ChevronRight, Calendar } from 'lucide-react';
import DatePicker, { registerLocale } from 'react-datepicker';
import { ptBR } from 'date-fns/locale';
import { createSearchParams } from 'react-router-dom';

import 'react-datepicker/dist/react-datepicker.css';

registerLocale('pt-BR', ptBR);

const initialDates = {
  date_start: moment().subtract(6, 'days').toDate(),
  date_end: moment().toDate(),
};

interface DateToolbarFilterImprovedProps {
  // eslint-disable-next-line no-unused-vars
  setFilters: (filters: URLSearchParams) => void;
  filters: URLSearchParams;
  showMonthYearOnly?: boolean;
}

export const DateToolbarFilterImproved = ({
  setFilters,
  filters,
  showMonthYearOnly = false,
}: DateToolbarFilterImprovedProps) => {
  const [dates, setDates] = useState<{ date_start: Date; date_end: Date }>(initialDates);
  const [currentMonth, setCurrentMonth] = useState(moment());
  const isCurrentMonth = currentMonth.isSame(moment(), 'month');

  useEffect(() => {
    const startDate = filters?.get('date_start') || initialDates.date_start;
    const endDate = filters?.get('date_end') || initialDates.date_end;

    if (startDate && endDate) {
      const startMoment = moment(startDate);
      const endMoment = moment(endDate);

      setDates({
        date_start: startMoment.toDate(),
        date_end: endMoment.toDate(),
      });

      // Define o mês atual baseado na data de início
      setCurrentMonth(startMoment);
    } else {
      setDates(initialDates);
      setCurrentMonth(moment());
    }
  }, [filters]);

  const handlePreviousMonth = () => {
    const newMonth = currentMonth.clone().subtract(1, 'month');
    setCurrentMonth(newMonth);

    if (showMonthYearOnly) {
      setDates({
        date_start: newMonth.startOf('month').toDate(),
        date_end: newMonth.endOf('month').toDate(),
      });

      setFilters(
        createSearchParams({
          ...Object.fromEntries(filters),
          date_start: newMonth.startOf('month').format('YYYY-MM-DD'),
          date_end: newMonth.endOf('month').format('YYYY-MM-DD'),
        }),
      );
    } else {
      setDates({
        date_start: newMonth.startOf('month').toDate(),
        date_end: newMonth.endOf('month').toDate(),
      });

      setFilters(
        createSearchParams({
          ...Object.fromEntries(filters),
          date_start: newMonth.startOf('month').format('YYYY-MM-DD'),
          date_end: newMonth.endOf('month').format('YYYY-MM-DD'),
        }),
      );
    }
  };

  const handleNextMonth = () => {
    const newMonth = currentMonth.clone().add(1, 'month');
    setCurrentMonth(newMonth);

    if (showMonthYearOnly) {
      setDates({
        date_start: newMonth.startOf('month').toDate(),
        date_end: newMonth.endOf('month').toDate(),
      });

      setFilters(
        createSearchParams({
          ...Object.fromEntries(filters),
          date_start: newMonth.startOf('month').format('YYYY-MM-DD'),
          date_end: newMonth.endOf('month').format('YYYY-MM-DD'),
        }),
      );
    } else {
      setDates({
        date_start: newMonth.startOf('month').toDate(),
        date_end: newMonth.endOf('month').toDate(),
      });

      setFilters(
        createSearchParams({
          ...Object.fromEntries(filters),
          date_start: newMonth.startOf('month').format('YYYY-MM-DD'),
          date_end: newMonth.endOf('month').format('YYYY-MM-DD'),
        }),
      );
    }
  };

  const handleDateSelect = (date: Date) => {
    if (showMonthYearOnly) {
      const firstDayOfMonth = moment(date).startOf('month');
      const lastDayOfMonth = moment(date).endOf('month');

      setDates({
        date_start: firstDayOfMonth.toDate(),
        date_end: lastDayOfMonth.toDate(),
      });

      setCurrentMonth(moment(date));

      setFilters(
        createSearchParams({
          ...Object.fromEntries(filters),
          date_start: firstDayOfMonth.format('YYYY-MM-DD'),
          date_end: lastDayOfMonth.format('YYYY-MM-DD'),
        }),
      );
    } else {
      const startOfMonth = moment(date).startOf('month');
      const endOfMonth = moment(date).endOf('month');

      setDates({
        date_start: startOfMonth.toDate(),
        date_end: endOfMonth.toDate(),
      });

      setCurrentMonth(moment(date));

      setFilters(
        createSearchParams({
          ...Object.fromEntries(filters),
          date_start: startOfMonth.format('YYYY-MM-DD'),
          date_end: endOfMonth.format('YYYY-MM-DD'),
        }),
      );
    }
  };

  return (
    <HStack
      spacing={2}
      bg="white"
      px={2}
      h="42px"
      borderRadius="lg"
      shadow="sm"
      w={{ base: '100%', md: 'auto' }}
      justify="center"
    >
      <IconButton
        aria-label="Mês anterior"
        icon={<Icon as={ChevronLeft} />}
        onClick={handlePreviousMonth}
        variant="ghost"
        size="sm"
        colorScheme="blue"
      />
      <Box position="relative">
        <DatePicker
          selected={dates.date_start}
          onChange={handleDateSelect}
          locale="pt-BR"
          dateFormat="MMMM 'de' yyyy"
          showMonthYearPicker={showMonthYearOnly}
          customInput={
            <HStack
              spacing={2}
              minW="180px"
              justify="center"
              cursor="pointer"
              _hover={{ bg: 'gray.50' }}
              px={2}
              py={1}
              borderRadius="md"
            >
              <Icon as={Calendar} color="gray.500" boxSize={4} />
              <Text fontWeight="medium" fontSize="sm" textTransform="capitalize" color="gray.700">
                {currentMonth.format('MMMM [de] YYYY')}
              </Text>
            </HStack>
          }
        />
      </Box>
      <IconButton
        aria-label="Próximo mês"
        icon={<Icon as={ChevronRight} />}
        onClick={handleNextMonth}
        variant="ghost"
        size="sm"
        colorScheme="blue"
        isDisabled={isCurrentMonth}
      />
    </HStack>
  );
};
